import { Box, Heading, Text, HStack, Icon } from "@chakra-ui/react";
import { MdRestaurant as RestaurantIcon, MdArrowBack as BackIcon } from "react-icons/md";
import { useLocation } from "wouter";
import LegalSection from "@/components/LegalSection";
import LegalP from "@/components/LegalP";
import LegalLi from "@/components/LegalLi";

export default function Terms() {
  const [, setLocation] = useLocation();

  return (
    <Box bg="bg.canvas" minH="100vh">
      {/* Header */}
      <Box
        as="header"
        bg="bg.surface"
        borderBottom="1px solid"
        borderColor="border.muted"
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Box h="env(safe-area-inset-top, 0px)" bg="accent.emphasis" />
        <Box
          px={{ base: 5, md: 8, lg: 12 }}
          py={3}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
        <HStack gap={2}>
          <Box
            bg="bg.surface"
            border="1px solid"
            borderColor="border.muted"
            borderRadius="7px"
            w="28px" h="28px"
            display="flex" alignItems="center" justifyContent="center"
            flexShrink={0}
          >
            <Icon as={RestaurantIcon} boxSize={4} color="accent.emphasis" />
          </Box>
          <Heading fontFamily="heading" fontWeight="800" fontSize="18px" color="text.primary">
            Byte <Text as="span" color="accent.emphasis">Pantry</Text>
          </Heading>
        </HStack>
        <Box
          as="button"
          display="flex"
          alignItems="center"
          gap={1}
          onClick={() => setLocation("/")}
          fontFamily="body"
          fontSize="13px"
          fontWeight="500"
          color="text.muted"
          cursor="pointer"
          _hover={{ color: "text.primary" }}
          border="none"
          bg="transparent"
          p={0}
        >
          <Icon as={BackIcon} boxSize={4} />
          Back
        </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box
        maxW={{ base: "100%", md: "680px" }}
        mx="auto"
        px={{ base: 5, md: 8 }}
        py={{ base: 10, md: 14 }}
      >
        <Heading
          fontFamily="heading"
          fontWeight="800"
          fontSize={{ base: "26px", md: "32px" }}
          color="text.primary"
          mb={2}
        >
          Terms of Service
        </Heading>
        <Text fontFamily="body" fontWeight="300" fontSize="13px" color="text.muted" mb={10}>
          Last updated: March 2026
        </Text>

        <LegalSection title="Accepting these terms">
          <LegalP>
            By creating an account or using Byte Pantry, you agree to these Terms of Service.
            If you do not agree, please do not use the service. We may update these terms from
            time to time — continued use after changes are posted means you accept the updated terms.
          </LegalP>
        </LegalSection>

        <LegalSection title="What Byte Pantry is">
          <LegalP>
            Byte Pantry is a personal recipe management service. It lets you save, organise, and
            cook from a private collection of recipes. You can import recipes from websites, photos,
            and text, and access them across your devices.
          </LegalP>
          <LegalP>
            The service is currently free. We reserve the right to introduce paid plans in the
            future, with reasonable notice to existing users.
          </LegalP>
        </LegalSection>

        <LegalSection title="Your account">
          <LegalLi>You must be 16 or older to use Byte Pantry.</LegalLi>
          <LegalLi>You are responsible for keeping your login credentials secure.</LegalLi>
          <LegalLi>You may only create one account per person.</LegalLi>
          <LegalLi>You may not use the service for any unlawful purpose.</LegalLi>
          <LegalLi>We may suspend or terminate accounts that violate these terms.</LegalLi>
        </LegalSection>

        <LegalSection title="Your content">
          <LegalP>
            Your recipes belong to you. By storing content in Byte Pantry, you grant us a limited
            licence to host, process, and display it solely for the purpose of providing the service
            to you.
          </LegalP>
          <LegalP>
            You are responsible for ensuring you have the right to store and use any recipe content
            you import. Byte Pantry is for personal use — please respect the copyright of recipe
            authors and publishers.
          </LegalP>
        </LegalSection>

        <LegalSection title="AI features">
          <LegalP>
            Byte Pantry uses AI to extract and structure recipes and to power the AI Chef assistant.
            AI-generated content may contain errors. Always use your judgement when following
            cooking instructions, particularly regarding allergens, temperatures, and food safety.
          </LegalP>
          <LegalP>
            We do not use your personal recipe data to train AI models without your explicit consent.
          </LegalP>
        </LegalSection>

        <LegalSection title="Acceptable use">
          <LegalP>You agree not to:</LegalP>
          <LegalLi>Attempt to access other users' data</LegalLi>
          <LegalLi>Scrape, reverse-engineer, or abuse the service or its APIs</LegalLi>
          <LegalLi>Upload content that is illegal, harmful, or infringes third-party rights</LegalLi>
          <LegalLi>Use the service in a way that could damage, disable, or impair it</LegalLi>
        </LegalSection>

        <LegalSection title="Availability and changes">
          <LegalP>
            We aim to keep Byte Pantry available at all times but cannot guarantee uninterrupted
            access. We may modify, suspend, or discontinue features with reasonable notice. We are
            not liable for any loss resulting from downtime or changes to the service.
          </LegalP>
        </LegalSection>

        <LegalSection title="Limitation of liability">
          <LegalP>
            Byte Pantry is provided "as is" without warranties of any kind. To the fullest extent
            permitted by law, we are not liable for any indirect, incidental, or consequential
            damages arising from your use of the service — including data loss, errors in
            AI-generated content, or service interruptions.
          </LegalP>
        </LegalSection>

        <LegalSection title="Governing law">
          <LegalP>
            These terms are governed by the laws of England and Wales. Any disputes shall be
            subject to the exclusive jurisdiction of the courts of England and Wales.
          </LegalP>
        </LegalSection>

        <LegalSection title="Contact">
          <LegalP>
            Questions about these terms? Email us at{" "}
            <Text as="span" color="accent.secondary" fontWeight="500">legal@byte-pantry.com</Text>.
          </LegalP>
        </LegalSection>

        <Box borderTop="1px solid" borderColor="border.muted" pt={8} mt={4}>
          <Text fontFamily="body" fontWeight="300" fontSize="12px" color="text.muted">
            &copy; 2026 Byte Pantry. All rights reserved.
          </Text>
        </Box>
      </Box>
    </Box>
  );
}
